import { SocialIcon } from '@change/ds-core-ui';
import { FacebookRegularIcon as FacebookIcon, InstagramIcon, RedditIcon } from '@change/ds-icons';

import type { TranslationKey } from '@change-corgi/core/i18n';

import type { PlatformDataMap } from 'src/app/pages/starterDashboardV2/shared/hooks';
import type { RecommendedGroup } from 'src/app/pages/starterDashboardV2/shared/types';

// TODO: Remove this eslint rule disable
// eslint-disable-next-line @change/sibling-module-imports
import { extractSharingResource } from '../../../contexts/ReadyToShareContext/utils/readyToShareDataMapping';
import { PostPreviewContainer } from '../components/PostPreviewContainer';
import { platformDataForFullPostType, rtsForFullPostType } from '../utils/readyToShareMapper';

import { stepsFactory } from './stepsFactory';
import type { FullPostType, SharingModalFactoryProps } from './types';

type ComponentsFactoryType = {
	iconComponent: () => React.JSX.Element;
	modalTitle: string;
	modalDescription: string;
	postPreviewComponent: () => React.JSX.Element;
	nextStepsComponent: () => React.JSX.Element;
	secondaryCTA: string | undefined;
	primaryCTA: string;
};

// Note: The following constants are exported for testing purposes only. They should not be used outside of this file.

type TranslateFn = (key: TranslationKey | string) => string;

export const ICONS = {
	facebook: (translate: TranslateFn) => (
		<SocialIcon
			icon={<FacebookIcon />}
			aria-label={translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.icons.facebook_aria_label')}
			variant="facebook"
		/>
	),
	instagram: (translate: TranslateFn) => (
		<SocialIcon
			icon={<InstagramIcon />}
			aria-label={translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.icons.instagram_aria_label')}
			variant="instagram"
		/>
	),
	reddit: (translate: TranslateFn) => (
		<SocialIcon
			icon={<RedditIcon />}
			aria-label={translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.icons.reddit_aria_label')}
			variant="reddit"
		/>
	),
};

export const TITLE_COPIES: Record<FullPostType, (translate: TranslateFn) => string> = {
	facebook_post: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.titles.facebook_post'),
	facebook_story: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.titles.facebook_story'),
	facebook_group_post: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.titles.facebook_group_post'),
	instagram_post: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.titles.instagram_post'),
	instagram_story: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.titles.instagram_story'),
	reddit_post: (translate) => translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.titles.reddit_post'),
	reddit_group_post: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.titles.reddit_group_post'),
};
export const DESCRIPTION_COPIES: Record<FullPostType, (translate: TranslateFn) => string> = {
	facebook_post: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.descriptions.facebook_post'),
	facebook_group_post: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.descriptions.facebook_group_post'),
	facebook_story: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.descriptions.facebook_story'),
	instagram_post: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.descriptions.instagram_post'),
	instagram_story: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.descriptions.instagram_story'),
	reddit_post: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.descriptions.reddit_post'),
	reddit_group_post: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.descriptions.reddit_group_post'),
};

const POST_PREVIEWS: Record<
	FullPostType,
	(props: SharingModalFactoryProps, dynamicData: PlatformDataMap) => React.JSX.Element
> = {
	facebook_post: (props, dynamicData) => {
		const fullPostType = 'facebook_post' as const;
		const platformData = platformDataForFullPostType(fullPostType, dynamicData);
		const readyToSharePost = rtsForFullPostType(fullPostType, dynamicData);
		const isLoading = platformData?.status === 'PENDING';

		// const readyToSharePost = dynamicData.readyToSharePostsValue?.facebook[0];
		const sharingResource = extractSharingResource('facebook_post', {
			post: readyToSharePost,
			petition: props.petition,
		});

		const postPreviewProps = {
			variant: 'facebook_post' as const,
			previewImageUrl: sharingResource.previewImageUrl,
			message: sharingResource.message,
			showDisclaimer: true,
			isLoading,
		};
		return <PostPreviewContainer {...postPreviewProps} />;
	},
	facebook_story: (props, dynamicData) => {
		const fullPostType = 'facebook_story' as const;
		const platformData = platformDataForFullPostType(fullPostType, dynamicData);
		const readyToSharePost = rtsForFullPostType(fullPostType, dynamicData);
		const isLoading = platformData?.status === 'PENDING';

		const sharingResource = extractSharingResource('facebook_story', {
			petition: props.petition,
			post: readyToSharePost,
		});

		const postPreviewProps = {
			variant: 'facebook_story' as const,
			previewImageUrl: sharingResource.previewImageUrl,
			shareUrl: sharingResource.shareUrl,
			showDisclaimer: false,
			isLoading,
		};
		return <PostPreviewContainer {...postPreviewProps} />;
	},
	facebook_group_post: (props, dynamicData) => {
		const fullPostType = 'facebook_group_post' as const;
		const platformData = platformDataForFullPostType(fullPostType, dynamicData);
		const readyToSharePost = rtsForFullPostType(fullPostType, dynamicData);
		const isLoading = platformData?.status === 'PENDING';
		const recommendedGroup = 'recommendedGroup' in props ? (props.recommendedGroup as RecommendedGroup) : undefined;
		const sharingResource = extractSharingResource('facebook_group_post', {
			post: readyToSharePost,
			petition: props.petition,
			recommendedGroup,
		});

		const postPreviewProps = {
			variant: 'facebook_group_post' as const,
			previewImageUrl: sharingResource.previewImageUrl,
			message: sharingResource.message,
			recommendedGroup,
			showDisclaimer: true,
			isLoading,
		};
		return <PostPreviewContainer {...postPreviewProps} />;
	},
	instagram_post: (props, dynamicData) => {
		const fullPostType = 'instagram_post' as const;
		const platformData = platformDataForFullPostType(fullPostType, dynamicData);
		const readyToSharePost = rtsForFullPostType(fullPostType, dynamicData);
		const isLoading = platformData?.status === 'PENDING';
		const sharingResource = extractSharingResource('instagram_post', {
			post: readyToSharePost,
			petition: props.petition,
		});

		const postPreviewProps = {
			variant: 'instagram_post' as const,
			previewImageUrl: sharingResource.previewImageUrl,
			message: sharingResource.message,
			showDisclaimer: true,
			isLoading,
		};
		return <PostPreviewContainer {...postPreviewProps} />;
	},
	instagram_story: (props, dynamicData) => {
		const fullPostType = 'instagram_story' as const;
		const platformData = platformDataForFullPostType(fullPostType, dynamicData);
		const readyToSharePost = rtsForFullPostType(fullPostType, dynamicData);
		const isLoading = platformData?.status === 'PENDING';

		const sharingResource = extractSharingResource('instagram_story', {
			petition: props.petition,
			post: readyToSharePost,
		});

		const postPreviewProps = {
			variant: 'instagram_story' as const,
			previewImageUrl: sharingResource.previewImageUrl,
			shareUrl: sharingResource.shareUrl,
			showDisclaimer: false,
			isLoading,
		};
		return <PostPreviewContainer {...postPreviewProps} />;
	},
	reddit_post: (props, dynamicData) => {
		const fullPostType = 'reddit_post' as const;
		const platformData = platformDataForFullPostType(fullPostType, dynamicData);
		const readyToSharePost = rtsForFullPostType(fullPostType, dynamicData);
		const isLoading = platformData?.status === 'PENDING';
		const recommendedGroup = 'recommendedGroup' in props ? (props.recommendedGroup as RecommendedGroup) : undefined;
		const sharingResource = extractSharingResource('reddit_post', {
			post: readyToSharePost,
			petition: props.petition,
		});

		const postPreviewProps = {
			variant: 'reddit_post' as const,
			previewImageUrl: sharingResource.previewImageUrl,
			message: sharingResource.message,
			recommendedGroup,
			showDisclaimer: true,
			petition: props.petition,
			isLoading,
		};
		return <PostPreviewContainer {...postPreviewProps} />;
	},
	reddit_group_post: (props, dynamicData) => {
		const fullPostType = 'reddit_post' as const;
		const platformData = platformDataForFullPostType(fullPostType, dynamicData);
		const readyToSharePost = rtsForFullPostType(fullPostType, dynamicData);
		const isLoading = platformData?.status === 'PENDING';
		const recommendedGroup = 'recommendedGroup' in props ? (props.recommendedGroup as RecommendedGroup) : undefined;
		const sharingResource = extractSharingResource('reddit_group_post', {
			post: readyToSharePost,
			petition: props.petition,
		});

		const postPreviewProps = {
			variant: 'reddit_post' as const,
			previewImageUrl: sharingResource.previewImageUrl,
			message: sharingResource.message,
			recommendedGroup,
			showDisclaimer: true,
			petition: props.petition,
			isLoading,
		};
		return <PostPreviewContainer {...postPreviewProps} />;
	},
};

export const SECONDARY_CTAS: Record<FullPostType, ((translate: TranslateFn) => string) | undefined> = {
	facebook_post: undefined,
	facebook_story: undefined,
	facebook_group_post: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.secondary_cta.facebook_group_post'),
	instagram_post: undefined,
	instagram_story: undefined,
	reddit_post: undefined,
	reddit_group_post: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.secondary_cta.reddit_group_post'),
};
export const PRIMARY_CTAS: Record<FullPostType, (translate: TranslateFn) => string> = {
	facebook_post: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.primary_cta.facebook_post'),
	facebook_story: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.primary_cta.facebook_story'),
	facebook_group_post: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.primary_cta.facebook_group_post'),
	instagram_post: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.primary_cta.instagram_post'),
	instagram_story: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.primary_cta.instagram_story'),
	reddit_post: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.primary_cta.reddit_post'),
	reddit_group_post: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.primary_cta.reddit_group_post'),
};

export function componentsFactory(
	props: SharingModalFactoryProps,
	dynamicData: PlatformDataMap,
	translate: TranslateFn,
): ComponentsFactoryType {
	let recommendedGroup;
	const { platform, postType } = props;

	if ('recommendedGroup' in props) {
		recommendedGroup = props.recommendedGroup;
	}

	const fullPostType = `${platform}${recommendedGroup ? '_group' : ''}_${postType}` as FullPostType;

	return {
		iconComponent: () => ICONS[platform](translate),
		modalTitle: TITLE_COPIES[fullPostType](translate),
		modalDescription: DESCRIPTION_COPIES[fullPostType](translate),
		postPreviewComponent: () => {
			return POST_PREVIEWS[fullPostType](props, dynamicData);
		},
		nextStepsComponent: () => {
			return stepsFactory(fullPostType, translate);
		},
		secondaryCTA: SECONDARY_CTAS[fullPostType]?.(translate),
		primaryCTA: PRIMARY_CTAS[fullPostType](translate),
	};
}
